import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { ArrowDownToLine, ArrowUpFromLine, RefreshCw } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import { useVpsStore } from '../../stores/vpsStore';

interface SyncIndicatorProps {
  label: string;
  icon: React.ReactNode;
  timestamp: string | null;
}

function formatTimestamp(iso: string | null): { relative: string; absolute: string } {
  if (!iso) return { relative: 'Never', absolute: '' };
  try {
    const date = parseISO(iso);
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: format(date, 'MMM d, h:mm a'),
    };
  } catch {
    return { relative: 'Unknown', absolute: '' };
  }
}

function SyncIndicator({ label, icon, timestamp }: SyncIndicatorProps) {
  const { relative, absolute } = formatTimestamp(timestamp);

  return (
    <div className="flex-1 rounded-lg bg-bg-tertiary p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-text-muted">{icon}</span>
        <span className="text-sm font-medium text-text-primary">{label}</span>
      </div>
      <p className="text-sm text-text-secondary">{relative}</p>
      {absolute ? (
        <p className="text-xs text-text-muted mt-0.5">{absolute}</p>
      ) : null}
    </div>
  );
}

export function SyncPanel() {
  const syncStatus = useVpsStore((s) => s.syncStatus);
  const syncTriggering = useVpsStore((s) => s.syncTriggering);
  const triggerSync = useVpsStore((s) => s.triggerSync);

  return (
    <div className="bg-bg-secondary border border-border-subtle rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-text-primary">Bilateral Sync</h3>
        <button
          onClick={triggerSync}
          disabled={syncTriggering}
          className="flex items-center gap-2 rounded-md bg-accent-purple px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-accent-purple/80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncTriggering ? (
            <Spinner size="sm" />
          ) : (
            <RefreshCw size={14} />
          )}
          Sync Now
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SyncIndicator
          label="From VPS → Local"
          icon={<ArrowDownToLine size={14} />}
          timestamp={syncStatus?.lastFromVps ?? null}
        />
        <SyncIndicator
          label="Local → VPS"
          icon={<ArrowUpFromLine size={14} />}
          timestamp={syncStatus?.lastToVps ?? null}
        />
        <SyncIndicator
          label="SecondBrain"
          icon={<RefreshCw size={14} />}
          timestamp={syncStatus?.lastSecondBrain ?? null}
        />
      </div>

      <p className="text-xs text-text-muted mt-3">
        Auto-sync every 15 minutes (launchd)
      </p>
    </div>
  );
}

export default SyncPanel;
